var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import { LOYALTY_OFFER_KEY, LOYALTY_URL_PARAM, OFFER_KEYS_URL_PARAM } from '../../utils/constants';
import hondaRestApi from '../apis/hondaRestApi';
import layoutServiceApi from '../apis/layoutServiceApi';
import isSSR from '../../utils/isSSR';
import { selectPrimaryCalculatorIncentiveOfferIdsParam, selectPrimaryCalculatorLoyaltyOfferKey, } from './financialCalculators';
import { selectCanUseLoyalty } from './offerOptions';
export var setExteriorColor = createAction('currentVehicle/setExteriorColor');
export var setInteriorColor = createAction('currentVehicle/setInteriorColor');
export var setColors = createAction('currentVehicle/setColors');
export var toggleAccessory = createAction('currentVehicle/toggleAccessory');
export var toggleAccessoryAddQueue = createAction('currentVehicle/toggleAccessoryAddQueue');
export var toggleAccessoryRemoveQueue = createAction('currentVehicle/toggleAccessoryRemoveQueue');
export var clearAccessoryAddQueue = createAction('currentVehicle/clearAccessoryAddQueue');
export var clearAccessoryRemoveQueue = createAction('currentVehicle/clearAccessoryRemoveQueue');
export var toggleProtection = createAction('currentVehicle/toggleProtection');
export var addProtectionAddOn = createAction('currentVehicle/addProtectionAddOn');
export var removeProtectionAddOn = createAction('currentVehicle/removeProtectionAddOn');
export var warrantySelectionChanged = createAction('currentVehicle/warrantySelectionChanged');
export var urlParsedOnInitialLoad = createAction('currentVehicle/urlParsedOnInitialLoad');
export var updateCurrentVehicleField = createAction('currentVehicle/updateCurrentVehicleField');
export var resetCurrentVehicle = createAction('currentVehicle/resetCurrentVehicle');
export var initialState = {
    modelKey: '',
    modelYear: '',
    modelCode: '',
    inventoryStatus: '',
    transmissionKey: '',
    trimKey: '',
    accessoryKeys: [],
    accessoryKeysAddQueue: '',
    accessoryKeysRemoveQueue: '',
    protectionKeys: [],
    protectionAddOnKeys: [],
    exteriorColorKey: '',
    interiorColorKey: '',
};
/**
 * currentVehicle is a single object that represents selections the user has made.
 * Should be tightly coupled to query params, to support deep linking and page refresh.
 */
var currentVehicleReducer = createReducer(initialState, function (builder) {
    builder
        .addCase(setColors, function (state, action) { return (__assign(__assign({}, state), { exteriorColorKey: action.payload.exteriorColor, interiorColorKey: action.payload.interiorColor })); })
        .addCase(setExteriorColor, function (state, action) { return (__assign(__assign({}, state), { exteriorColorKey: action.payload })); })
        .addCase(setInteriorColor, function (state, action) { return (__assign(__assign({}, state), { interiorColorKey: action.payload })); })
        .addCase(toggleAccessory, function (state, action) {
        var _a;
        if ((_a = state.accessoryKeys) === null || _a === void 0 ? void 0 : _a.includes(action.payload)) {
            return __assign(__assign({}, state), { accessoryKeys: state.accessoryKeys.filter(function (key) { return key !== action.payload; }) });
        }
        return __assign(__assign({}, state), { accessoryKeys: __spreadArray(__spreadArray([], state.accessoryKeys, true), [action.payload], false) });
    })
        .addCase(toggleProtection, function (state, action) {
        var _a, _b;
        if ((_a = state.protectionKeys) === null || _a === void 0 ? void 0 : _a.includes(action.payload)) {
            return __assign(__assign({}, state), { protectionKeys: state.protectionKeys.filter(function (key) { return key !== action.payload; }), protectionAddOnKeys: (_b = state.protectionAddOnKeys) === null || _b === void 0 ? void 0 : _b.filter(function (key) { return key !== action.payload; }) });
        }
        return __assign(__assign({}, state), { protectionKeys: __spreadArray(__spreadArray([], state.protectionKeys, true), [action.payload], false) });
    })
        .addCase(addProtectionAddOn, function (state, action) {
        var _a;
        if ((_a = state.protectionAddOnKeys) === null || _a === void 0 ? void 0 : _a.includes(action.payload)) {
            return state;
        }
        return __assign(__assign({}, state), { protectionAddOnKeys: state.protectionAddOnKeys
                ? __spreadArray(__spreadArray([], state.protectionAddOnKeys, true), [action.payload], false) : [action.payload] });
    })
        .addCase(removeProtectionAddOn, function (state, action) {
        var _a;
        if ((_a = state.protectionAddOnKeys) === null || _a === void 0 ? void 0 : _a.includes(action.payload)) {
            return __assign(__assign({}, state), { protectionAddOnKeys: state.protectionAddOnKeys.filter(function (key) { return key !== action.payload; }) });
        }
    })
        .addCase(toggleAccessoryAddQueue, function (state, action) { return (__assign(__assign({}, state), { accessoryKeysAddQueue: action.payload })); })
        .addCase(toggleAccessoryRemoveQueue, function (state, action) { return (__assign(__assign({}, state), { accessoryKeysRemoveQueue: action.payload })); })
        .addCase(clearAccessoryAddQueue, function (state) { return (__assign(__assign({}, state), { accessoryKeysAddQueue: '' })); })
        .addCase(clearAccessoryRemoveQueue, function (state) { return (__assign(__assign({}, state), { accessoryKeysRemoveQueue: '' })); })
        .addCase(urlParsedOnInitialLoad, function (state, action) { return (__assign(__assign({}, state), action.payload)); })
        .addCase(updateCurrentVehicleField, function (state, action) { return (__assign(__assign({}, state), action.payload)); })
        .addCase(resetCurrentVehicle, function () { return initialState; })
        .addMatcher(layoutServiceApi.endpoints.getColorByTransmission.matchFulfilled, function (state, action) {
        var _a, _b;
        if ((_a = action.payload) === null || _a === void 0 ? void 0 : _a.modelCode) {
            return __assign(__assign({}, state), { modelCode: (_b = action.payload) === null || _b === void 0 ? void 0 : _b.modelCode });
        }
        return state;
    })
        .addMatcher(hondaRestApi.endpoints.getInventoryStatus.matchFulfilled, function (state, action) {
        var res = action.payload.find(function (i) { return i.modelCode === state.modelCode; });
        if (res === null || res === void 0 ? void 0 : res.inventoryStatus) {
            return __assign(__assign({}, state), { inventoryStatus: res === null || res === void 0 ? void 0 : res.inventoryStatus });
        }
        return state;
    });
});
// TODO: why are these keys in CurrentVehicle since they are not sync to URL?
var paramKeysToOmit = ['accessoryKeysAddQueue', 'accessoryKeysRemoveQueue'];
var shouldUpdateParam = function (key) { return !paramKeysToOmit.includes(key); };
export var updateQueryStringFromCurrentVehicle = function (state) {
    if (isSSR())
        return;
    var currentUrl = new URL(window.location.href);
    Object.entries(state).forEach(function (_a) {
        var key = _a[0], value = _a[1];
        // casting to keyof CurrentVehicle, as TS infer key to be wilder string type
        if (shouldUpdateParam(key)) {
            currentUrl.searchParams.set(key, Array.isArray(value) ? value.join() : value);
        }
    });
    window.history.pushState({}, '', currentUrl.toString());
};
export var selectCurrentVehicle = function (state) { return state === null || state === void 0 ? void 0 : state.currentVehicle; };
export var selectCurrentVehicleModelYear = function (state) { var _a; return (_a = state === null || state === void 0 ? void 0 : state.currentVehicle) === null || _a === void 0 ? void 0 : _a.modelYear; };
export var selectCurrentVehicleModelKey = function (state) { var _a; return (_a = state === null || state === void 0 ? void 0 : state.currentVehicle) === null || _a === void 0 ? void 0 : _a.modelKey; };
export var selectCurrentVehicleTrimKey = function (state) { var _a; return (_a = state === null || state === void 0 ? void 0 : state.currentVehicle) === null || _a === void 0 ? void 0 : _a.trimKey; };
export var selectCurrentVehicleTransmissionKey = function (state) { var _a; return (_a = state === null || state === void 0 ? void 0 : state.currentVehicle) === null || _a === void 0 ? void 0 : _a.transmissionKey; };
export var selectCurrentVehicleExteriorColorKey = function (state) { var _a; return (_a = state === null || state === void 0 ? void 0 : state.currentVehicle) === null || _a === void 0 ? void 0 : _a.exteriorColorKey; };
export var selectCurrentVehicleInteriorColorKey = function (state) { var _a; return (_a = state === null || state === void 0 ? void 0 : state.currentVehicle) === null || _a === void 0 ? void 0 : _a.interiorColorKey; };
export var selectCurrentVehicleAccessoryKeys = function (state) { var _a; return (_a = state === null || state === void 0 ? void 0 : state.currentVehicle) === null || _a === void 0 ? void 0 : _a.accessoryKeys; };
export var selectCurrentVehicleProtectionKeys = function (state) { var _a; return (_a = state === null || state === void 0 ? void 0 : state.currentVehicle) === null || _a === void 0 ? void 0 : _a.protectionKeys; };
export var selectCurrentVehicleAccessoryKeysAddQueue = function (state) { var _a; return (_a = state === null || state === void 0 ? void 0 : state.currentVehicle) === null || _a === void 0 ? void 0 : _a.accessoryKeysAddQueue; };
export var selectCurrentVehicleAccessoryKeysRemoveQueue = function (state) { var _a; return (_a = state === null || state === void 0 ? void 0 : state.currentVehicle) === null || _a === void 0 ? void 0 : _a.accessoryKeysRemoveQueue; };
export var selectCurrentVehicleUrlParams = createSelector([
    selectCurrentVehicle,
    selectPrimaryCalculatorLoyaltyOfferKey,
    selectCanUseLoyalty,
    selectPrimaryCalculatorIncentiveOfferIdsParam,
], function (currentVehicle, loyaltyOfferKey, canUseLoyalty, offerIds) {
    var _a, _b;
    var _c, _d, _e;
    return (__assign((_a = { modelKey: currentVehicle.modelKey, modelYear: currentVehicle.modelYear, transmissionKey: currentVehicle.transmissionKey, trimKey: currentVehicle.trimKey, exteriorColorKey: currentVehicle.exteriorColorKey, interiorColorKey: currentVehicle.interiorColorKey, accessoryKeys: (_c = currentVehicle.accessoryKeys) === null || _c === void 0 ? void 0 : _c.join(','), protectionKeys: (_d = currentVehicle.protectionKeys) === null || _d === void 0 ? void 0 : _d.join(','), protectionAddOnKeys: (_e = currentVehicle.protectionAddOnKeys) === null || _e === void 0 ? void 0 : _e.join(','), inventoryStatus: currentVehicle.inventoryStatus }, _a[OFFER_KEYS_URL_PARAM] = offerIds, _a), (canUseLoyalty ? (_b = {}, _b[LOYALTY_OFFER_KEY] = loyaltyOfferKey, _b[LOYALTY_URL_PARAM] = 'true', _b) : {})));
});
export var selectCurrentVehicleUrlQueryString = createSelector([selectCurrentVehicleUrlParams], function (currentVehicleUrlParams) {
    try {
        var searchParams_1 = new URLSearchParams();
        var existingSearchParams = new URLSearchParams(isSSR() ? '' : window.location.search);
        Object.entries(__assign({ buildId: existingSearchParams.get('buildId') }, currentVehicleUrlParams)).forEach(function (_a) {
            var key = _a[0], value = _a[1];
            var result = Array.isArray(value) ? value.join(',') : value;
            if (result && typeof result === 'string') {
                searchParams_1.set(key, result);
            }
        });
        return searchParams_1.toString();
    }
    catch (error) {
        return '';
    }
});
export var persistCurrentVehicleToUrl = function () { return function (dispatch, getState) {
    var currentVehicle = getState().currentVehicle;
    if (currentVehicle) {
        updateQueryStringFromCurrentVehicle(currentVehicle);
    }
}; };
export default currentVehicleReducer;
